import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react-lite'
import { useForm } from 'react-hook-form'
import { useLandingPageViewmodel } from '@/features/landing/landing-page.hooks'
import {
  getDefaultLandingLeadFormValues,
  type LandingLeadFormValues,
  landingLeadFormSchema,
} from '@/features/landing/model/landing-lead-form.schema'
import { LandingLeadInput } from '@/features/landing/view/LandingLeadInput'
import { cn } from '@/shared/lib/cn'
import { Button } from '@/shared/ui/Button'

type LandingLeadFormProps = {
  className?: string
}

export const LandingLeadForm = observer(function LandingLeadForm({ className }: LandingLeadFormProps) {
  const landingPageViewmodel = useLandingPageViewmodel()
  const formCopy = {
    companyLabel: 'Company or project',
    companyPlaceholder: 'Optional, but useful',
    emailBodyCompany: 'Company or project',
    emailBodyEmail: 'Email',
    emailBodyName: 'Name',
    emailBodyNotProvided: 'Not provided',
    emailBodySource: 'Source',
    emailBodySourceSuffix: 'page',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    mailSubjectPrefix: 'Lead',
    messageLabel: 'What needs to happen?',
    messagePlaceholder: 'Short version: what exists, what is broken, and what you want from me.',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    privacyNote: 'This opens a direct email draft. No newsletter trap.',
    submitLabel: 'Open email draft',
    submittedBody: 'Send it from your email client so it actually reaches the inbox.',
    submittedTitle: 'Draft opened.',
    validationMessage: 'Fix the marked fields and try again.',
  }
  const form = useForm<LandingLeadFormValues>({
    defaultValues: getDefaultLandingLeadFormValues(),
    resolver: zodResolver(landingLeadFormSchema),
  })

  const onSubmit = form.handleSubmit((values) => {
    landingPageViewmodel.captureLead(values)
    form.reset(getDefaultLandingLeadFormValues())
  })

  return (
    <section className={cn('contact-panel p-6', className)} id="lead">
      <h2 className="text-xl font-semibold text-phosphor-ivory">{landingPageViewmodel.page.content.leadFormTitle}</h2>
      <p className="mt-2 text-sm leading-7 text-white/64">{landingPageViewmodel.page.content.leadFormIntro}</p>
      <form className="mt-6 space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
        <LandingLeadInput error={form.formState.errors.name?.message} label={formCopy.nameLabel}>
          <input
            autoComplete="name"
            className="contact-input"
            placeholder={formCopy.namePlaceholder}
            {...form.register('name')}
          />
        </LandingLeadInput>
        <LandingLeadInput error={form.formState.errors.email?.message} label={formCopy.emailLabel}>
          <input
            autoComplete="email"
            className="contact-input"
            placeholder={formCopy.emailPlaceholder}
            type="email"
            {...form.register('email')}
          />
        </LandingLeadInput>
        <LandingLeadInput error={form.formState.errors.company?.message} label={formCopy.companyLabel}>
          <input
            autoComplete="organization"
            className="contact-input"
            placeholder={formCopy.companyPlaceholder}
            {...form.register('company')}
          />
        </LandingLeadInput>
        <LandingLeadInput error={form.formState.errors.message?.message} label={formCopy.messageLabel}>
          <textarea
            className="contact-input min-h-32 resize-y"
            placeholder={formCopy.messagePlaceholder}
            {...form.register('message')}
          />
        </LandingLeadInput>
        {landingPageViewmodel.hasSubmittedLead ? (
          <p className="contact-feedback contact-feedback-success" role="status">
            <strong className="block text-phosphor-ivory">{formCopy.submittedTitle}</strong>
            {formCopy.submittedBody}
          </p>
        ) : null}
        <Button className="w-full justify-center" size="lg" type="submit">
          {formCopy.submitLabel}
        </Button>
        <p className="text-xs leading-6 text-white/46">{formCopy.privacyNote}</p>
      </form>
    </section>
  )
})
